type ColumnVisibilityMode = 'hide-empty' | 'show-all';
const ROOT_MEMBER_NAMES = new Set([
  'EquityMember',
  'FundsAndReservesMember',
  'PropertyPlantAndEquipmentMember',
  'IntangibleAssetsMember',
]);

function parseNumberish(value: string): number | null {
  const v = value.replace(/,/g, '').trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isZeroLike(value: string): boolean {
  const n = parseNumberish(value);
  if (n === null) return true;
  return Math.abs(n) < 1e-12;
}

function getToggleableColumnIndices(table: HTMLTableElement): number[] {
  const headers = Array.from(table.querySelectorAll('thead th'));
  if (headers.length <= 1) return [];

  // SOCIE: first column = labels, others are members (no Total column)
  if (table.classList.contains('socie-table')) {
    return headers.map((_, i) => i).slice(1);
  }

  // Sum calculation: first column = labels, last = Total
  if (table.classList.contains('sum-calculation-table')) {
    return headers.map((_, i) => i).slice(1, -1);
  }

  // Default: skip first column only
  return headers.map((_, i) => i).slice(1);
}

function getPinnedRootColumnIndices(table: HTMLTableElement, cols: number[]): Set<number> {
  const pinned = new Set<number>();
  const rows = Array.from(table.querySelectorAll('tbody tr'));

  for (const colIdx of cols) {
    for (const row of rows) {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length <= colIdx) continue;

      const input = cells[colIdx].querySelector('input[data-equity-member]') as HTMLInputElement | null;
      const memberName = input?.getAttribute('data-equity-member');
      if (memberName && ROOT_MEMBER_NAMES.has(memberName)) {
        pinned.add(colIdx);
        break;
      }
    }
  }

  return pinned;
}

function shouldShowColumn(table: HTMLTableElement, columnIndex: number): boolean {
  const rows = Array.from(table.querySelectorAll('tbody tr'));
  for (const row of rows) {
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length <= columnIndex) continue;

    const cell = cells[columnIndex];
    const input = cell.querySelector('input') as HTMLInputElement | null;
    if (!input) continue;

    const raw = (input.value || '').trim();
    if (!raw) continue;

    // Treat 0/0.00 as empty (matches 1.0 behavior for SOCIE + sum-calculation)
    if (!isZeroLike(raw)) return true;
  }
  return false;
}

function setColumnDisplay(table: HTMLTableElement, columnIndex: number, display: string) {
  const headerRow = table.querySelector('thead tr');
  if (headerRow) {
    const headers = Array.from(headerRow.querySelectorAll('th'));
    if (headers[columnIndex]) (headers[columnIndex] as HTMLElement).style.display = display;
  }

  const rows = Array.from(table.querySelectorAll('tbody tr'));
  rows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells[columnIndex]) (cells[columnIndex] as HTMLElement).style.display = display;
  });
}

export function applyHideEmptyColumns(table: HTMLTableElement) {
  const cols = getToggleableColumnIndices(table);
  const pinnedRootCols = getPinnedRootColumnIndices(table, cols);
  let visibleDynamicCols = 0;

  cols.forEach((colIdx) => {
    // Keep root/total member columns visible even if values are all zero/empty.
    if (pinnedRootCols.has(colIdx)) {
      setColumnDisplay(table, colIdx, '');
      return;
    }

    const show = shouldShowColumn(table, colIdx);
    if (show) visibleDynamicCols += 1;
    setColumnDisplay(table, colIdx, show ? '' : 'none');
  });

  // Safety fallback for unexpected structures with no recognized root member:
  // never hide every member column.
  if (visibleDynamicCols === 0 && pinnedRootCols.size === 0 && cols.length > 0) {
    const fallbackCol = cols[cols.length - 1];
    setColumnDisplay(table, fallbackCol, '');
  }

  table.dataset.columnVisibilityMode = 'hide-empty';
}

export function applyShowAllColumns(table: HTMLTableElement) {
  const cols = getToggleableColumnIndices(table);
  cols.forEach((colIdx) => setColumnDisplay(table, colIdx, ''));
  table.dataset.columnVisibilityMode = 'show-all';
}

export function toggleColumnVisibilityForSection(sectionEl: HTMLElement) {
  const table = sectionEl.querySelector('table') as HTMLTableElement | null;
  if (!table) return;

  const current = (table.dataset.columnVisibilityMode as ColumnVisibilityMode | undefined) || 'hide-empty';
  const next: ColumnVisibilityMode = current === 'hide-empty' ? 'show-all' : 'hide-empty';

  if (next === 'show-all') {
    applyShowAllColumns(table);
  } else {
    applyHideEmptyColumns(table);
  }

  const btn = sectionEl.querySelector('.table-toggle-button') as HTMLButtonElement | null;
  if (btn) {
    btn.textContent = next === 'show-all' ? 'Hide Empty Columns' : 'Show All Columns';
  }
}


